import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { z } from "zod";
import { sendContactSupportEmailSendContactSupportEmailPost } from "@/client";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";

const emailSchema = z.object({
  subject: z
    .string()
    .min(1, {
      message: "Subject is required.",
    })
    .max(100, {
      message: "Subject must be less than 100 characters.",
    }),
  message: z
    .string()
    .min(10, {
      message: "Message must be at least 10 characters.",
    })
    .max(1000, {
      message: "Message must be less than 1000 characters.",
    }),
});

export const Route = createFileRoute("/_authenticated/support")({
  component: ContactSupportPage,
});

function ContactSupportPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { mutateAsync: sendContactSupportEmail, isPending } = useMutation({
    mutationFn: sendContactSupportEmailSendContactSupportEmailPost,
    onSuccess: () => {
      toast({
        title: t("Message sent successfully!"),
        description: t("We'll get back to you as soon as possible."),
      });
      form.reset();
    },
    onError: () => {
      toast({
        title: t("Error sending message"),
        description: t("Please try again later."),
        variant: "destructive",
      });
    },
  });

  const form = useForm<z.infer<typeof emailSchema>>({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      subject: "",
      message: "",
    },
  });

  function onSubmit(data: z.infer<typeof emailSchema>) {
    sendContactSupportEmail({
      requestBody: {
        subject: data.subject,
        message: data.message,
      },
    });
  }

  return (
    <div className="container mx-auto max-w-2xl py-8">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="subject"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("Subject")}</FormLabel>
                <FormControl>
                  <Input placeholder={t("Brief description of your issue")} {...field} />
                </FormControl>
                <FormDescription>{t("Please provide a brief subject for your message.")}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="message"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Message</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder={t("Please describe your issue in detail...")}
                    className="min-h-[120px]"
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  {t("Please provide detailed information about your issue or question.")}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex justify-end">
            <Button type="submit" disabled={isPending}>
              {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : t("Send Message")}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
